import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ThemeToggleButton } from "@/components/ui/theme-toggle";
import { useAuth } from "@/contexts/auth-context";
import { useLanguage } from "@/contexts/language-context";
import {
  LayoutDashboard,
  Wallet,
  TrendingUp,
  Target,
  FileText,
  Bell,
  Settings,
  LogOut,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavbarProps {
  unreadNotifications?: number;
}

/**
 * Navigation Bar Component
 * Desktop: full nav with active pill accent.
 * Mobile: slim bar (main items live in the bottom nav).
 */
export function Navbar({ unreadNotifications = 0 }: NavbarProps) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getUserInitials = () => {
    if (!user?.username) return "U";
    return user.username
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const navigation = [
    {
      name: t("navbar.dashboard"),
      path: "/",
      icon: LayoutDashboard,
    },
    {
      name: t("navbar.budget"),
      path: "/budget",
      icon: Wallet,
    },
    {
      name: t("navbar.netWorth"),
      path: "/net-worth",
      icon: TrendingUp,
    },
    {
      name: t("navbar.goals"),
      path: "/goals",
      icon: Target,
    },
    {
      name: t("navbar.reports"),
      path: "/reports",
      icon: FileText,
    },
  ];

  const secondaryNavigation = [
    {
      name: t("navbar.notifications"),
      path: "/notifications",
      icon: Bell,
    },
    {
      name: t("navbar.settings"),
      path: "/settings",
      icon: Settings,
    },
  ];

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const navButtonClass = (active: boolean) =>
    "gap-2 rounded-full " +
    (active
      ? "bg-primary/15 text-primary font-medium hover:bg-primary/20 hover:text-primary"
      : "text-muted-foreground hover:text-foreground hover:bg-muted/60");

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-14 sm:h-16 items-center justify-between px-3 sm:px-4">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary/15 flex items-center justify-center">
            <Wallet className="h-5 w-5 text-primary" />
          </div>
          <span className="text-lg sm:text-xl font-bold font-display bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            <span className="hidden sm:inline">Personal Finance</span>
            <span className="sm:hidden">Finance</span>
          </span>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navigation.map((item) => (
            <Button
              key={item.path}
              variant="ghost"
              onClick={() => navigate(item.path)}
              size="sm"
              className={navButtonClass(isActive(item.path))}
            >
              <item.icon className="h-4 w-4" />
              <span>{item.name}</span>
            </Button>
          ))}

          <Separator orientation="vertical" className="h-6 mx-2" />

          {secondaryNavigation.map((item) => (
            <Button
              key={item.path}
              variant="ghost"
              onClick={() => navigate(item.path)}
              size="sm"
              className={navButtonClass(isActive(item.path)) + " relative"}
            >
              <item.icon className="h-4 w-4" />
              <span>{item.name}</span>
              {item.path === "/notifications" && unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                  {unreadNotifications > 9 ? "9+" : unreadNotifications}
                </span>
              )}
            </Button>
          ))}

          <Separator orientation="vertical" className="h-6 mx-2" />

          <ThemeToggleButton />

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
                  <Avatar className="h-10 w-10 rounded-full">
                    <AvatarFallback className="bg-primary/10 text-primary rounded-full">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user.username || "User"}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user.email || ""}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("navbar.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Mobile — slim bar; main navigation lives in BottomNav */}
        <div className="flex md:hidden items-center gap-2">
          <ThemeToggleButton />
          {user && (
            <Avatar className="h-8 w-8 sm:h-9 sm:w-9 rounded-full">
              <AvatarFallback className="bg-primary/10 text-primary text-xs sm:text-sm rounded-full">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      </div>
    </nav>
  );
}